<template>
  <div class="public-content space-y-12 pb-12 md:space-y-20 md:pb-20">
    <section id="home">
      <UiHero
        image="/images/slides/4.jpg"
        image-alt="Équipe en intervention préventive sur événement"
      >
        <template #eyebrow>
          Dispositifs médicaux préventifs en Belgique
        </template>
        <template #title>
          Votre événement couvert par
          <span class="public-gradient-text">des secouristes qualifiés.</span>
        </template>
        <template #lead>
          Poste de soins, secouristes, ambulance ou VIM, dimensionnés selon votre affluence et vos risques.
        </template>
        <template #actions>
          <UButton
            to="/devis"
            size="xl"
            label="Demander un devis"
            class="justify-center"
          />
          <UButton
            to="/prestations"
            variant="link"
            size="xl"
            trailing-icon="i-heroicons-arrow-right"
            class="justify-center"
            label="Voir nos prestations"
          />
        </template>
        <template #caption>
          <strong class="font-semibold text-[var(--ink-900)]">Depuis 2017.</strong>
          Plus de 180 volontaires, 3 centres, Wallonie et Bruxelles.
        </template>
      </UiHero>

      <div class="mx-auto mt-2 max-w-7xl px-6 md:px-12 xl:px-6">
        <dl class="grid grid-cols-2 gap-x-6 gap-y-6 border-y border-[var(--line-soft)] py-6 md:grid-cols-4">
          <div>
            <dt class="public-muted text-sm">
              Expérience
            </dt>
            <dd class="mt-1 font-display text-3xl font-extrabold text-[var(--ink-900)]">
              {{ yearsExpertise }} ans
            </dd>
          </div>
          <div>
            <dt class="public-muted text-sm">
              Volontaires
            </dt>
            <dd class="mt-1 font-display text-3xl font-extrabold text-[var(--ink-900)]">
              180+
            </dd>
          </div>
          <div>
            <dt class="public-muted text-sm">
              Centres
            </dt>
            <dd class="mt-1 font-display text-3xl font-extrabold text-[var(--ink-900)]">
              3
            </dd>
          </div>
          <div>
            <dt class="public-muted text-sm">
              Préventifs en 2025
            </dt>
            <dd class="mt-1 font-display text-3xl font-extrabold text-[var(--ink-900)]">
              510+
            </dd>
          </div>
        </dl>

        <UiPartners class="mt-8" />
      </div>
    </section>

    <UiQuoteQuickStart />

    <UiMobileBookBar
      to="#devis-rapide"
      label="Demander un devis"
      phone="+32474035548"
    />

    <section
      id="reviews"
      class="public-content"
    >
      <div class="mx-auto max-w-7xl px-6 md:px-12 xl:px-6">
        <div class="mb-8 md:mb-10">
          <h2 class="public-h2">
            Ce que disent les organisateurs.
          </h2>
        </div>
        <div v-reveal>
          <LazyVisibleUiTestimonials :hydrate-on-visible="{ rootMargin: '300px' }" />
        </div>
      </div>
    </section>

    <section id="profils">
      <div class="mx-auto max-w-7xl px-6 md:px-12 xl:px-6">
        <h2 class="public-h2">
          Que cherchez-vous ?
        </h2>
        <div
          class="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          data-track-zone="profiles"
        >
          <NuxtLink
            v-for="profile in profiles"
            :key="profile.to"
            :to="profile.to"
            class="public-card group flex flex-col gap-2 p-5 transition-colors hover:border-brand-blue-400"
          >
            <UIcon
              :name="profile.icon"
              class="h-8 w-8 text-brand-blue-600"
            />
            <span class="public-h3 mt-2">{{ profile.title }}</span>
            <span class="public-muted text-sm leading-relaxed">{{ profile.text }}</span>
            <span class="mt-auto pt-2 text-sm font-semibold text-brand-blue-700 dark:text-brand-blue-300">{{ profile.cta }} →</span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <section id="services">
      <div class="mx-auto max-w-7xl px-6 md:px-12 xl:px-6">
        <div class="grid gap-10 lg:grid-cols-12 lg:gap-14">
          <div class="lg:col-span-5">
            <h2 class="public-h2">
              Une architecture de secours adaptée à votre événement.
            </h2>
            <p class="public-lead mt-4">
              Notre méthode combine prévention, réactivité et lisibilité opérationnelle, avec des moyens calibrés selon l'affluence, les risques et le contexte local.
            </p>
          </div>

          <div class="grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:col-span-7">
            <article
              v-reveal="0"
              class="border-t border-[var(--line-soft)] pt-5"
            >
              <UIcon
                name="i-heroicons-shield-check-20-solid"
                class="h-9 w-9 text-brand-blue-600"
              />
              <h3 class="public-h3 mt-4">
                Plan de couverture médicale
              </h3>
              <p class="public-muted mt-2 text-sm leading-relaxed">
                Analyse des risques, plan d'implantation et protocoles d'alerte avant événement.
              </p>
            </article>

            <article
              v-reveal="1"
              class="border-t border-[var(--line-soft)] pt-5"
            >
              <UIcon
                name="i-heroicons-heart-20-solid"
                class="h-9 w-9 text-brand-red-500"
              />
              <h3 class="public-h3 mt-4">
                Équipes secouristes terrain
              </h3>
              <p class="public-muted mt-2 text-sm leading-relaxed">
                Présence proactive, évaluation initiale et prise en charge immédiate des incidents.
              </p>
            </article>

            <article
              v-reveal="2"
              class="border-t border-[var(--line-soft)] pt-5"
            >
              <UIcon
                name="i-heroicons-truck-20-solid"
                class="h-9 w-9 text-brand-blue-600"
              />
              <h3 class="public-h3 mt-4">
                Moyens mobiles préventifs
              </h3>
              <p class="public-muted mt-2 text-sm leading-relaxed">
                Ambulances événementielles et VIM intégrés au dispositif prévisionnel.
              </p>
            </article>

            <article
              v-reveal="3"
              class="border-t border-[var(--line-soft)] pt-5"
            >
              <UIcon
                name="i-heroicons-command-line-20-solid"
                class="h-9 w-9 text-brand-blue-600"
              />
              <h3 class="public-h3 mt-4">
                Coordination opérationnelle
              </h3>
              <p class="public-muted mt-2 text-sm leading-relaxed">
                Pilotage en temps réel, liaison organisateur et adaptation dynamique des ressources.
              </p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section id="deroule">
      <div class="mx-auto max-w-7xl px-6 md:px-12 xl:px-6">
        <h2 class="public-h2">
          Comment ça se passe.
        </h2>
        <ol class="mt-8 grid gap-8 md:grid-cols-3">
          <li
            v-for="(step, index) in steps"
            :key="step.title"
            class="border-t border-[var(--line-soft)] pt-5"
          >
            <span class="font-display text-3xl font-extrabold text-brand-blue-600">0{{ index + 1 }}</span>
            <h3 class="public-h3 mt-2">
              {{ step.title }}
            </h3>
            <p class="public-muted mt-2 text-sm leading-relaxed">
              {{ step.text }}
            </p>
          </li>
        </ol>
      </div>
    </section>

    <section id="family">
      <div class="mx-auto max-w-7xl px-6 md:px-12 xl:px-6">
        <div
          v-reveal
          class="grid gap-8 md:grid-cols-2 md:items-center lg:gap-14"
        >
          <div>
            <h2 class="public-h2">
              Une équipe engagée, une exécution fiable.
            </h2>
            <p class="public-lead mt-4">
              Depuis 2017, nos volontaires et professionnels de santé construisent une réponse médicale événementielle rigoureuse, humaine et constante.
            </p>
            <ul class="mt-6 space-y-3">
              <li class="flex items-start gap-3 text-[var(--ink-900)]">
                <UIcon
                  name="i-heroicons-check-circle-20-solid"
                  class="mt-0.5 h-5 w-5 shrink-0 text-brand-blue-600"
                />
                Priorité absolue à la sécurité des participants.
              </li>
              <li class="flex items-start gap-3 text-[var(--ink-900)]">
                <UIcon
                  name="i-heroicons-check-circle-20-solid"
                  class="mt-0.5 h-5 w-5 shrink-0 text-brand-blue-600"
                />
                Standards opérationnels clairs, coordination sans friction.
              </li>
            </ul>
          </div>

          <NuxtImg
            src="/images/slides/9.jpg"
            alt="Équipe Ambulance Présent 2.0"
            width="720"
            height="520"
            densities="x1"
            format="webp"
            class="aspect-[4/3] w-full rounded-[var(--radius-section)] border border-[var(--line-soft)] object-cover md:order-first"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section
      id="formations"
      class="public-content"
    >
      <div class="mx-auto max-w-7xl px-6 md:px-12 xl:px-6">
        <div class="mb-8 flex flex-col gap-4 md:mb-10 md:flex-row md:items-end md:justify-between">
          <h2 class="public-h2">
            Prochaines formations.
          </h2>
          <UButton
            to="/formations"
            variant="link"
            trailing-icon="i-heroicons-arrow-right"
            label="Toutes nos formations"
            class="px-0"
          />
        </div>
        <LazyVisibleTrainingUpcomingSessions :hydrate-on-visible="{ rootMargin: '300px' }" />
      </div>
    </section>

    <section id="faq">
      <div class="mx-auto max-w-3xl px-6 md:px-12 xl:px-6">
        <h2 class="public-h2">
          Questions fréquentes.
        </h2>
        <LazyVisibleUAccordion
          :hydrate-on-visible="{ rootMargin: '300px' }"
          :items="faqItems"
          :ui="{ item: 'public-card px-5 py-3 mb-3' }"
          class="mt-8 w-full"
        />
      </div>
    </section>

    <section class="public-content">
      <div class="mx-auto max-w-7xl px-6 md:px-12 xl:px-6">
        <div class="border-t border-[var(--line-soft)] pt-8">
          <p class="public-muted mb-4 text-sm font-semibold">
            Découvrir aussi
          </p>
          <div class="flex flex-wrap gap-3">
            <NuxtLink
              v-for="link in discoverLinks"
              :key="link.to"
              :to="link.to"
              class="inline-flex items-center rounded-full border border-[var(--line-soft)] bg-[var(--surface-1)] px-4 py-2 text-sm font-medium text-[var(--ink-900)] transition-colors hover:border-brand-blue-400 hover:text-brand-blue-700 dark:hover:text-brand-blue-300"
            >
              {{ link.label }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <LazyVisibleUiQuote :hydrate-on-visible="{ rootMargin: '300px' }" />
    <LazyVisibleUiContact :hydrate-on-visible="{ rootMargin: '300px' }" />
  </div>
</template>

<script lang="ts" setup>
const yearsExpertise = (new Date().getFullYear()) - 2017

const steps = [
  { title: 'Vous décrivez votre événement', text: 'Type, date, lieu et affluence attendue : le formulaire de devis prend quelques minutes.' },
  { title: 'Nous dimensionnons le dispositif', text: 'Analyse des risques, choix des moyens (secouristes, poste de soins, ambulance, VIM) et plan d\'implantation.' },
  { title: 'Nos équipes sont sur place', text: 'Présence terrain le jour J, coordination avec vous et prise en charge immédiate des incidents.' },
]

const faqItems = [
  { label: 'Dans quelles régions intervenez-vous ?', content: 'Nous mettons en place des dispositifs médicaux préventifs sur-mesure dans les provinces de Hainaut, Namur, Liège et Luxembourg ainsi qu\'à Bruxelles, selon le format, l\'affluence et le niveau de risque de votre événement.' },
  { label: 'Quel dispositif faut-il pour mon événement ?', content: 'Nous calibrons les moyens selon votre public, la durée, l\'environnement et le niveau de risque attendu.' },
  { label: 'Proposez-vous des ambulances et des VIM ?', content: 'Oui. Nous pouvons déployer des ambulances sur dispositif médical préventif, des équipes secouristes et des moyens médicalisés dont VIM selon les exigences réglementaires.' },
  { label: 'Comment obtenir un devis ?', content: 'Utilisez notre formulaire de devis en ligne. Nous analysons votre besoin pour proposer une solution adaptée, réactive et conforme.' },
]

const profiles = [
  { to: '/devis', icon: 'i-heroicons-calendar-days-20-solid', title: 'J\'organise un événement', text: 'Poste de secours, équipe secouriste, ambulance ou VIM.', cta: 'Demander un devis' },
  { to: '/formations', icon: 'i-heroicons-academic-cap-20-solid', title: 'Je veux me former', text: 'Premiers secours, BLS-AED, formations pour entreprises et particuliers.', cta: 'Voir les formations' },
  { to: '/guide-defibrillateur-dae-entreprise-belgique', icon: 'i-heroicons-bolt-20-solid', title: 'Je m\'équipe d\'un DAE', text: 'Obligations, choix et entretien d\'un défibrillateur en entreprise.', cta: 'Lire le guide' },
  { to: '/postuler', icon: 'i-heroicons-heart-20-solid', title: 'Je veux devenir volontaire', text: 'Rejoignez une équipe de secouristes engagés.', cta: 'Postuler' },
]

const discoverLinks = [
  { to: '/prestations', label: 'Nos prestations →' },
  { to: '/formations', label: 'Formations premiers secours →' },
  { to: '/postuler', label: 'Postuler →' },
  { to: '/qui-sommes-nous', label: 'Qui sommes-nous →' },
  { to: '/contact', label: 'Nous contacter →' },
]

const pageTitle = 'Dispositif Médical Préventif & Poste de Secours Événementiel en Belgique'
const seoTitle = 'Dispositif Médical Préventif & Poste de Secours Événementiel en Belgique'
const seoDescription = 'Ambulance Présent 2.0 sécurise vos événements en Wallonie et à Bruxelles : postes de secours, équipes secouristes, ambulances, VIM et dispositifs médicaux préventifs.'

useHead({
  title: pageTitle,
})

useSeoMeta({
  description: seoDescription,
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
})

defineOgImage('Image.takumi', {
  title: seoTitle,
  description: seoDescription,
})

const siteUrl = useRuntimeConfig().public.appUrl || 'https://ambulancepresent.be'

useSchemaOrg([
  defineWebPage({
    name: seoTitle,
    description: seoDescription,
    url: siteUrl,
    inLanguage: 'fr-BE',
  }),
  {
    '@type': 'FAQPage',
    'mainEntity': faqItems.map(item => ({
      '@type': 'Question',
      'name': item.label,
      'acceptedAnswer': { '@type': 'Answer', 'text': item.content },
    })),
  },
])
</script>
